import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

// Layouts
import PublicLayout from './components/layout/PublicLayout';
import DashboardLayout from './components/layout/DashboardLayout';

// Public pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';

// Protected pages
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import Payments from './pages/Payments';
import Escrow from './pages/Escrow';
import Chargebacks from './pages/Chargebacks';
import Settings from './pages/Settings';
import Integration from './pages/Integration';

/**
 * Route guard for authenticated users.
 * Redirects to /login if not authenticated.
 */
function RequireAuth({ children }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

/**
 * Route guard for guests only.
 * Redirects authenticated users to /dashboard.
 */
function RequireGuest({ children }) {
  const { isAuthenticated, hasCompletedOnboarding } = useAuth();
  if (isAuthenticated) {
    return <Navigate to={hasCompletedOnboarding ? '/dashboard' : '/onboarding'} replace />;
  }
  return children;
}

/**
 * Onboarding guard: authenticated but onboarding not yet complete.
 */
function RequireOnboarding({ children }) {
  const { isAuthenticated, hasCompletedOnboarding } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (hasCompletedOnboarding) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Landing />} />
        <Route
          path="/login"
          element={
            <RequireGuest>
              <Login />
            </RequireGuest>
          }
        />
        <Route
          path="/signup"
          element={
            <RequireGuest>
              <Signup />
            </RequireGuest>
          }
        />
      </Route>

      {/* Onboarding */}
      <Route
        path="/onboarding"
        element={
          <RequireOnboarding>
            <Onboarding />
          </RequireOnboarding>
        }
      />

      {/* Protected dashboard routes */}
      <Route
        element={
          <RequireAuth>
            <DashboardLayout />
          </RequireAuth>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/dashboard/payments" element={<Payments />} />
        <Route path="/dashboard/escrow" element={<Escrow />} />
        <Route path="/dashboard/chargebacks" element={<Chargebacks />} />
        <Route path="/dashboard/settings" element={<Settings />} />
        <Route path="/dashboard/integration" element={<Integration />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
